import type { Account, AssuranceMode, Contact } from "@veto/domain";
import type { ProfileMedia } from "@veto/protocol";
import { runOnboarding, runUnlock, type SessionBundle } from "../messaging/accountLifecycle";
import { addContactFlow } from "../messaging/addContact";
import { takeInstanceLease } from "../instanceLock";
import type { SettingsTab } from "../screens/SettingsPanel";

export function createAccountHandlers(deps: {
  session: SessionBundle | null;
  account: Account | null;
  setVaultKeyB64: (v: string | null) => void;
  setAccount: (a: Account | null) => void;
  setSession: (s: SessionBundle | null) => void;
  setScreen: (s: "loading" | "onboarding" | "lock" | "app" | "error") => void;
  setToast: (t: string | null) => void;
  setShowAdd: (v: boolean) => void;
  setSettingsTab: (t: SettingsTab) => void;
  setShowSettings: (v: boolean) => void;
  refresh: () => Promise<void>;
  openConversation: (id: string) => Promise<void>;
}) {
  async function completeOnboarding(input: {
    displayName: string;
    pin: string;
    assuranceMode: AssuranceMode;
    avatar?: ProfileMedia | null;
  }) {
    const result = await runOnboarding(input);
    await takeInstanceLease(); // review H3: one unlocked window
    deps.setVaultKeyB64(result.vaultKeyB64);
    deps.setAccount(result.account);
    deps.setSession(result.session);
    await deps.refresh();
    deps.setScreen("app");
    deps.setToast(result.toast);
  }

  async function unlock(pin: string) {
    const result = await runUnlock(pin);
    if (!result) {
      deps.setScreen("onboarding");
      return;
    }
    // Review H3: take over from any other unlocked window BEFORE sessions load.
    await takeInstanceLease();
    deps.setVaultKeyB64(result.vaultKeyB64);
    deps.setAccount(result.account);
    deps.setSession(result.session);
    await deps.refresh();
    deps.setScreen("app");
    // Notifications are offered once, by the "Know when someone writes" sheet (AppShell).
  }

  async function addContact(
    displayName: string,
    hybridPubHex: string,
    messagingPubHex: string,
    transportPubHex?: string,
  ) {
    if (!deps.session || !deps.account) return;
    const { conversationId, verificationState } = await addContactFlow({
      session: deps.session,
      account: deps.account,
      displayName,
      hybridPubHex,
      messagingPubHex,
      transportPubHex,
      onKeyChange: () => {
        // FR-CON-003: visible alert + persisted security event (addContactFlow).
        // Do not auto-open Settings — that modal blocks Conversation details
        // Mark verified (FR-CON-002) and the in-chat Keys changed banner.
        deps.setToast("Security alert: contact keys changed — confirm safety number");
        deps.setSettingsTab("privacy");
      },
    });
    await deps.refresh();
    deps.setShowAdd(false);
    await deps.openConversation(conversationId);
    if (verificationState !== "changed")
      deps.setToast("Contact added — optional: verify in details for maximum safety");
  }

  return { completeOnboarding, unlock, addContact };
}
